import React, { useState, useMemo } from "react";
import LowStockView from "./LowStockView";
import EditIcon from "@mui/icons-material/Edit";
import EditLowStockModal from "../../utility/EditLowStockModal";
import { useMutation } from "@tanstack/react-query";
import { updateData } from "../../utility/adminApi";
import ImageWithPlaceholder from "../../components/ImageWithPlaceholder";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";

function LowStockTableBody({ 
  lowstockData, 
  currency, 
  fetchLowstock, 
  itemsPerPage = 10 
}) {
  const [newData, setNewData] = useState({});
  const [editTab, setEditTab] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleEdit = (item) => {
    setNewData(item);
    setEditTab(true);
  };

  const updateStock = useMutation({
    mutationFn: (data) => updateData("products/update/", data?.id, data),
    onSuccess: () => {
      setEditTab(false);
      fetchLowstock(1, "");
    },
  });

  const handleSubmit = (data) => {
    updateStock.mutate(data);
  };

  // Frontend pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return lowstockData?.slice(startIndex, endIndex) || [];
  }, [lowstockData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil((lowstockData?.length || 0) / itemsPerPage);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      <tbody>
        {paginatedData?.map((item) => (
          <tr
            key={item?.id}
            className="hover:bg-gray-100 border-b border-gray-300"
          >
            <td className="py-2  flex justify-center">
              <ImageWithPlaceholder
                item={item}
                className="w-10 h-10 rounded-md object-cover"
                placeholderClassName="w-10 h-10 bg-slate-400 text-white flex justify-center font-medium items-center rounded-md text-xl"
              />{" "}
            </td>
            <td className="py-2 px-4 text-center text-wrap">
              {item?.product_name}
            </td>
            <td className="py-2 px-4 text-center text-wrap">
              {item?.available_quantity}
            </td>
            <td className="py-2 px-4 text-center text-wrap">
              {currency} {formatNumbersWithDecimal(item?.price)}
            </td>

            <td className="py-2 px-4 text-center text-wrap">
              {item?.category?.cat_name}
            </td>
            <td className="py-2 px-4 flex justify-center  gap-3 text-wrap">
              <EditIcon
                onClick={() => handleEdit(item)}
                className="text-blue-500 cursor-pointer"
              />
              <LowStockView item={item} currency={currency} />
            </td>
          </tr>
        ))}
      </tbody>
      
      {/* Frontend Pagination Controls */}
      {totalPages > 1 && (
        <tfoot className="px-4">
          <tr>
            <td colSpan="6" className="py-4">
              <div className="flex justify-between items-center w-full gap-4 px-4">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className="p-3 bg-darkblue text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({lowstockData?.length || 0} total items)
                  </span>
                </div>
                
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className="p-3 bg-darkblue text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                >
                  Next
                </button>
              </div>
            </td>
          </tr>
        </tfoot>
      )}
      
      {editTab && (
        <EditLowStockModal
          newData={newData}
          setEditTab={setEditTab}
          setNewData={setNewData}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
}

export default LowStockTableBody;
